// Persists the calendar's source/event-type filter chips via GeneralPreference
// (server-side, so it survives across devices/sessions). Adds hydration +
// debounced persistence on top of the existing chip UI/filtering.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppSelector } from '@user/store/hooks'
import { toast } from '@sales-center/components'
import { logger } from '@user/utils/logger'
import { useDebounce } from '@user/utils/useDebounce'
import {
  IUserPreferenceGeneralListItemType,
  selectUserPreferencesGeneralLoaded,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import type { IUserPreferenceGeneralListItem } from '@user/store/userPreferences/userPreferencesGeneral'
import { DEFAULT_FILTERS, isVisible } from './useEventFilters'
import type { EventFilters, FilterKey } from './useEventFilters'
import type { CalendarEvent } from '../typings'
import { useQueuedPreferenceWrite } from './generalPreferencesLock'

/** The "Sales Center:" chip group — the plain-events chip plus its 3 sub-types. */
export const SC_EVENT_TYPE_KEYS: readonly FilterKey[] = [
  'sales_center',
  'activity_reminder',
  'next_action_date',
  'expected_close_date',
]

/** The external-source chip group, after the divider. Outlook joins once its chip ships (TC-13). */
export const SOURCE_KEYS: readonly FilterKey[] = ['pestpac', 'google']

const ALL_KEYS: readonly FilterKey[] = [...SC_EVENT_TYPE_KEYS, ...SOURCE_KEYS]

const NONE_SENTINEL = 'none'

// Not `_` (MapView's convention): FilterKey values like `next_action_date`
// are themselves compound with underscores, so splitting on `_` corrupts
// them. A comma never appears in any FilterKey.
const TOKEN_SEPARATOR = ','

/**
 * Decodes one preference row's raw value into per-key booleans for the given
 * group. `undefined` (row absent) defaults every key to selected; the `'none'`
 * sentinel (present-but-empty) defaults every key to unselected — the two are
 * never conflated. Tokens outside `knownKeys` are silently ignored (TC-11).
 */
export function decodeFilterGroup(
  rawValue: string | undefined,
  knownKeys: readonly FilterKey[],
): Partial<EventFilters> {
  const result: Partial<EventFilters> = {}

  if (rawValue === undefined) {
    knownKeys.forEach((key) => {
      result[key] = true
    })
    return result
  }

  if (rawValue === NONE_SENTINEL) {
    knownKeys.forEach((key) => {
      result[key] = false
    })
    return result
  }

  const tokens = new Set(rawValue.split(TOKEN_SEPARATOR).filter(Boolean))
  knownKeys.forEach((key) => {
    result[key] = tokens.has(key)
  })
  return result
}

/** Inverse of `decodeFilterGroup` — an empty selection persists as the explicit `'none'` sentinel, never as an empty string (TC-6). */
export function encodeFilterGroup(
  filters: EventFilters,
  knownKeys: readonly FilterKey[],
): string {
  const selected = knownKeys.filter((key) => filters[key])
  return selected.length > 0 ? selected.join(TOKEN_SEPARATOR) : NONE_SENTINEL
}

function findPrefValue(
  prefs: IUserPreferenceGeneralListItem[],
  type: IUserPreferenceGeneralListItemType,
): string | undefined {
  return prefs.find((row) => row?.type === type)?.value
}

function decodeAllGroups(
  prefs: IUserPreferenceGeneralListItem[],
): EventFilters {
  return {
    ...DEFAULT_FILTERS,
    ...decodeFilterGroup(
      findPrefValue(
        prefs,
        IUserPreferenceGeneralListItemType.UnifiedCalendarFilteredSCEventTypes,
      ),
      SC_EVENT_TYPE_KEYS,
    ),
    ...decodeFilterGroup(
      findPrefValue(
        prefs,
        IUserPreferenceGeneralListItemType.UnifiedCalendarFilteredSources,
      ),
      SOURCE_KEYS,
    ),
  } as EventFilters
}

function filtersEqual(a: EventFilters, b: EventFilters): boolean {
  return ALL_KEYS.every((key) => a[key] === b[key])
}

export type UseCalendarFilterPreferencesResult = {
  filters: EventFilters
  toggle: (key: FilterKey) => void
  visible: (event: CalendarEvent) => boolean
}

export function useCalendarFilterPreferences(): UseCalendarFilterPreferencesResult {
  const prefs = useAppSelector(userGeneralPreferencesList)
  const loaded = useAppSelector(selectUserPreferencesGeneralLoaded)
  // `userGeneralPreferencesList` returns a new array reference on every Redux
  // dispatch app-wide -- read via a ref so the hydration effect only depends
  // on `loaded`.
  const prefsRef = useRef(prefs)
  prefsRef.current = prefs

  const [filters, setFilters] = useState<EventFilters>(DEFAULT_FILTERS)
  // Latest filters, independent of the debounce below -- read on unmount to
  // flush a toggle made just before navigating away (the debounce timer
  // never fires once the hook unmounts).
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  const hydrated = useRef(false)
  // Tracks the last value we hydrated from or persisted to, so hydration itself
  // never fires a redundant write (TC-1: "no persistence call forced by mere load").
  const lastKnownRef = useRef<EventFilters>(DEFAULT_FILTERS)
  // A toggle made before prefs finish loading must win over hydration -- see
  // the matching comment on toggle() below.
  const userInteractedRef = useRef(false)

  useEffect(() => {
    if (!loaded || hydrated.current) return
    hydrated.current = true
    const decoded = decodeAllGroups(prefsRef.current)
    lastKnownRef.current = decoded
    // The window is small (prefs preload at the app root), but a toggle made
    // in it would otherwise be silently overwritten by the server value the
    // moment hydration catches up a beat later.
    if (!userInteractedRef.current) setFilters(decoded)
  }, [loaded])

  const toggle = useCallback((key: FilterKey) => {
    userInteractedRef.current = true
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const visible = useCallback(
    (event: CalendarEvent) => isVisible(event, filters),
    [filters],
  )

  // Debounced so a burst of toggles collapses into one write (TC-4),
  // mitigating the read-modify-write race in UpdateGeneralPreferencesBatch.
  const debouncedFilters = useDebounce(filters, 500)

  const handlePersistError = useCallback((err: unknown) => {
    // Persistence failure must not disrupt the already-applied optimistic UI
    // (TC-12) — log and move on, never throw or leave a rejection unhandled.
    logger.error('Failed to persist calendar filter preferences:', err)
    // Surfaced once per failed save (not per retry): the chips stay as the
    // user set them (TC-12) but would otherwise silently revert on the next
    // load with no indication anything went wrong.
    toast.error('Failed to save your calendar filter selection.')
  }, [])
  const persistRows = useQueuedPreferenceWrite(handlePersistError)

  const persist = useCallback(
    (next: EventFilters) => {
      persistRows([
        {
          type: IUserPreferenceGeneralListItemType.UnifiedCalendarFilteredSCEventTypes,
          value: encodeFilterGroup(next, SC_EVENT_TYPE_KEYS),
        },
        {
          type: IUserPreferenceGeneralListItemType.UnifiedCalendarFilteredSources,
          value: encodeFilterGroup(next, SOURCE_KEYS),
        },
      ])
    },
    [persistRows],
  )

  // Skip this effect's very first run: when prefs are already loaded before
  // mount (the real app path -- preferences load once at the root), the
  // hydration effect above and this one both fire in the same flush, so this
  // effect would otherwise still see the pre-hydration `debouncedFilters`
  // (DEFAULT_FILTERS) and persist it over the just-hydrated saved selection.
  const hasRunPersistenceEffectRef = useRef(false)
  useEffect(() => {
    if (!hasRunPersistenceEffectRef.current) {
      hasRunPersistenceEffectRef.current = true
      return
    }
    if (!hydrated.current) return
    if (filtersEqual(debouncedFilters, lastKnownRef.current)) return
    lastKnownRef.current = debouncedFilters
    persist(debouncedFilters)
  }, [debouncedFilters, persist])

  // Flush a still-pending toggle on unmount (see filtersRef above) so
  // navigating away within the debounce window doesn't drop the change.
  useEffect(() => {
    return () => {
      if (!hydrated.current) return
      if (filtersEqual(filtersRef.current, lastKnownRef.current)) return
      lastKnownRef.current = filtersRef.current
      persist(filtersRef.current)
    }
  }, [persist])

  return { filters, toggle, visible }
}
